import useAppDispatch from 'js/hooks/useAppDispatch';
import { useCallback } from 'react';
import {
  getProjects,
  assignFlowToProject,
  assignFlowsToProject,
  createProjectAssignFlow,
} from 'js/actions/projects';

type Options = {
  aclQueryMode?: string;
};

type AssignFlowToProjectParams = {
  id: number;
  projectId: number;
};

type AssignFlowsToProjectParams = {
  originNodeIds: number[];
  projectId: number;
};

type CreateProjectAssignFlowParams = {
  flowNodeId?: number | number[];
  newInfo?: {
    name?: string;
    description?: string;
    [key: string]: any;
  };
};

interface ProjectAdapterHookReturn {
  requestProjects: (
    page: number,
    sortCriteria: string,
    availableFilters: any,
    options?: Options,
  ) => Promise<any>;
  assignFlowToProject: (params: AssignFlowToProjectParams) => Promise<any>;
  assignFlowsToProject: (params: AssignFlowsToProjectParams) => Promise<any>;
  createProjectAssignFlow: (
    flowNodeId?: number | number[],
    newInfo?: CreateProjectAssignFlowParams['newInfo'],
  ) => Promise<any>;
}

function useProjectAdapter(): ProjectAdapterHookReturn {
  const dispatch = useAppDispatch();

  const requestProjects = useCallback(
    async (page: number, sortCriteria: string, availableFilters: any, options?: Options) =>
      dispatch(getProjects(page, sortCriteria, availableFilters, options || {})),
    [dispatch],
  );

  const assignFlowToProjectFn = useCallback(
    async (params: AssignFlowToProjectParams) => dispatch(assignFlowToProject(params)),
    [dispatch],
  );

  const assignFlowsToProjectFn = useCallback(
    async (params: AssignFlowsToProjectParams) => dispatch(assignFlowsToProject(params)),
    [dispatch],
  );

  const createProjectAssignFlowFn = useCallback(
    async (flowNodeId?: number | number[], newInfo?: CreateProjectAssignFlowParams['newInfo']) =>
      dispatch(createProjectAssignFlow(flowNodeId, newInfo)),
    [dispatch],
  );

  return {
    requestProjects,
    assignFlowToProject: assignFlowToProjectFn,
    assignFlowsToProject: assignFlowsToProjectFn,
    createProjectAssignFlow: createProjectAssignFlowFn,
  };
}

export { useProjectAdapter };
